import { isPublicLocale, publicLocales, type Locale } from "./routing";

export interface LanguageInfo {
  code: Locale;
  nativeName: string;
  flagCode: string;
  ogLocale: string;
}

export const LANGUAGES: LanguageInfo[] = [
  { code: "ko", nativeName: "한국어", flagCode: "kr", ogLocale: "ko_KR" },
  { code: "en", nativeName: "English", flagCode: "us", ogLocale: "en_US" },
  { code: "ja", nativeName: "日本語", flagCode: "jp", ogLocale: "ja_JP" },
  { code: "zh-CN", nativeName: "简体中文", flagCode: "cn", ogLocale: "zh_CN" },
  { code: "zh-TW", nativeName: "繁體中文", flagCode: "tw", ogLocale: "zh_TW" },
];

/**
 * 언어 선택기에 보일 언어 — 공개 언어 + (숨김 언어 URL 로 들어온 경우) 현재 언어.
 * 숨김 출시 중인 언어는 직접 URL 로만 접근하고 선택지에는 나오지 않는다.
 */
export function getSelectableLanguages(current?: string): LanguageInfo[] {
  return LANGUAGES.filter((lang) => isPublicLocale(lang.code) || lang.code === current);
}

// 헤더 언어 버튼용 짧은 표기 (flagCode와 다름 — en은 US 국기지만 표기는 EN)
export const LOCALE_SHORT_CODES: Record<Locale, string> = {
  ko: "KR",
  en: "EN",
  ja: "JP",
  "zh-CN": "CN",
  "zh-TW": "TW",
};

// 특정 언어 정보 조회 헬퍼
export const getLanguageInfo = (code: Locale): LanguageInfo | undefined =>
  LANGUAGES.find((lang) => lang.code === code);

/** locale → OG locale 매핑 (예: "ko" → "ko_KR") */
export const getOgLocale = (code: string): string =>
  LANGUAGES.find((lang) => lang.code === code)?.ogLocale || "en_US";

/** hreflang alternates 객체 생성 (예: { ko: "/ko", en: "/en", ... }) — 공개 언어만 */
export function buildHreflangAlternates(pathSuffix = ""): Record<string, string> {
  const alternates: Record<string, string> = {};
  for (const locale of publicLocales) {
    alternates[locale] = `/${locale}${pathSuffix}`;
  }
  alternates["x-default"] = `/en${pathSuffix}`;
  return alternates;
}

/** 절대 URL hreflang alternates 객체 생성 — 공개 언어만 */
export function buildHreflangAlternatesAbsolute(
  baseUrl: string,
  pathSuffix: string,
): Record<string, string> {
  const alternates: Record<string, string> = {};
  for (const locale of publicLocales) {
    alternates[locale] = `${baseUrl}/${locale}${pathSuffix}`;
  }
  alternates["x-default"] = `${baseUrl}/en${pathSuffix}`;
  return alternates;
}
